<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: User Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.userName')"
                v-model="filterOptions.user_name"
              />
              <!-- End:: User Name Input -->

              <!-- Start:: Mobile Number Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.mobileNumber')"
                v-model="filterOptions.mobile_number"
              />
              <!-- End:: Mobile Number Input -->

              <!-- Start:: User Type Filter -->
              <base-select-input
                col="3"
                :optionsList="userTypes"
                :placeholder="$t('PLACEHOLDERS.userType')"
                v-model="filterOptions.user_type"
              />
              <!-- End:: User Type Filter -->

              <!-- Start:: Transaction Type Filter -->
              <base-select-input
                col="3"
                :optionsList="transactionTypes"
                :placeholder="$t('PLACEHOLDERS.transactionType')"
                v-model="filterOptions.transaction_type"
              />
              <!-- End:: Transaction Type Filter -->

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.startDate')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.endDate')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Financial Summary Cards =========== -->
      <div class="financial_summary_wrapper">
        <div class="summary_card total_income">
          <div class="card_icon">
            <i class="fal fa-dollar-sign"></i>
          </div>
          <div class="card_content">
            <h6>{{ $t("PLACEHOLDERS.totalIncomeAllUsers") }}</h6>
            <p class="amount">
              {{
                new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(summaryData.total_income || 0)
              }}
              {{ $t("PLACEHOLDERS.sar") }}
            </p>
          </div>
        </div>

        <div class="summary_card individual_income">
          <div class="card_icon">
            <i class="fal fa-user"></i>
          </div>
          <div class="card_content">
            <h6>{{ $t("PLACEHOLDERS.totalIncomeIndividuals") }}</h6>
            <p class="amount">
              {{
                new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(summaryData.individual_users_income || 0)
              }}
              {{ $t("PLACEHOLDERS.sar") }}
            </p>
          </div>
        </div>

        <div class="summary_card store_income">
          <div class="card_icon">
            <i class="fal fa-store"></i>
          </div>
          <div class="card_content">
            <h6>{{ $t("PLACEHOLDERS.totalIncomeStores") }}</h6>
            <p class="amount">
              {{
                new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(summaryData?.service_providers_income || 0)
              }}
              {{ $t("PLACEHOLDERS.sar") }}
            </p>
          </div>
        </div>

        <div class="summary_card tax_income">
          <div class="card_icon">
            <i class="fal fa-receipt"></i>
          </div>
          <div class="card_content">
            <h6>{{ $t("PLACEHOLDERS.totalTaxValue") }}</h6>
            <p class="amount">
              {{
                new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(summaryData?.total_vat_amount || 0)
              }}
              {{ $t("PLACEHOLDERS.sar") }}
            </p>
          </div>
        </div>

        <div class="summary_card system_profit">
          <div class="card_icon">
            <i class="fal fa-chart-line"></i>
          </div>
          <div class="card_content">
            <h6>{{ $t("PLACEHOLDERS.netSystemProfit") }}</h6>
            <p class="amount">
              {{
                new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(summaryData.system_net_profit || 0)
              }}
              {{ $t("PLACEHOLDERS.sar") }}
            </p>
          </div>
        </div>

        <div class="summary_card transactions_count">
          <div class="card_icon">
            <i class="fal fa-file-invoice-dollar"></i>
          </div>
          <div class="card_content">
            <h6>{{ $t("PLACEHOLDERS.totalTransactionsCount") }}</h6>
            <p class="amount">
              {{ summaryData.total_transactions_count || 0 }}
            </p>
          </div>
        </div>
      </div>
      <!--  =========== End:: Financial Summary Cards =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("TITLES.financialReports") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcel"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>

          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
            :disabled="pdfDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ index }">
          <span>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </span>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: User Name -->
        <template v-slot:[`item.user_name`]="{ item }">
          <p v-if="!item.user_name" class="blue-grey--text text--darken-1">-</p>
          <p v-else>{{ item.user_name }}</p>
        </template>
        <!-- End:: User Name -->

        <!-- Start:: Mobile Number -->
        <template v-slot:[`item.mobile_number`]="{ item }">
          <p v-if="!item.mobile_number" class="blue-grey--text text--darken-1">
            -
          </p>
          <p v-else>{{ item.mobile_number }}</p>
        </template>
        <!-- End:: Mobile Number -->

        <!-- Start:: User Type -->
        <template v-slot:[`item.user_type`]="{ item }">
          <v-chip
            :color="getUserTypeColor(item.user_type)"
            text-color="white"
            small
          >
            {{ getUserTypeText(item.user_type) }}
          </v-chip>
        </template>
        <!-- End:: User Type -->

        <!-- Start:: Transaction Type -->
        <template v-slot:[`item.transactionable_type`]="{ item }">
          <v-chip
            :color="getTransactionTypeColor(item.transactionable_type)"
            text-color="white"
            small
          >
            {{ item.transactionable_type }}
          </v-chip>
        </template>
        <!-- End:: Transaction Type -->

        <!-- Start:: Package Name -->
        <template v-slot:[`item.package_name`]="{ item }">
          <p v-if="!item.package_name" class="blue-grey--text text--darken-1">
            -
          </p>
          <p v-else>{{ item.package_name }}</p>
        </template>
        <!-- End:: Package Name -->

        <!-- Start:: Number of Days -->
        <template v-slot:[`item.number_days`]="{ item }">
          <p v-if="!item.number_days" class="blue-grey--text text--darken-1">
            -
          </p>
          <p v-else>{{ item.number_days }}</p>
        </template>
        <!-- End:: Number of Days -->

        <!-- Start:: Number of Attendance -->
        <template v-slot:[`item.number_attendance`]="{ item }">
          <p
            v-if="!item.number_attendance"
            class="blue-grey--text text--darken-1"
          >
            -
          </p>
          <p v-else>{{ item.number_attendance }}</p>
        </template>
        <!-- End:: Number of Attendance -->

        <!-- Start:: Original Price -->
        <template v-slot:[`item.original_price`]="{ item }">
          <p class="font-weight-bold">
            {{ item.original_price }} {{ $t("PLACEHOLDERS.sar") }}
          </p>
        </template>
        <!-- End:: Original Price -->

        <!-- Start:: Tax Value -->
        <template v-slot:[`item.tax_in_value`]="{ item }">
          <p class="font-weight-bold text-warning">
            {{ item.tax_in_value }} {{ $t("PLACEHOLDERS.sar") }}
          </p>
        </template>
        <!-- End:: Tax Value -->

        <!-- Start:: Total Price -->
        <template v-slot:[`item.total_price`]="{ item }">
          <p class="font-weight-bold text-success">
            {{ item.total_price }} {{ $t("PLACEHOLDERS.sar") }}
          </p>
        </template>
        <!-- End:: Total Price -->

        <!-- Start:: System Profit -->
        <template v-slot:[`item.system_profit`]="{ item }">
          <p class="font-weight-bold text-primary">
            {{ item.system_profit }} {{ $t("PLACEHOLDERS.sar") }}
          </p>
        </template>
        <!-- End:: System Profit -->

        <!-- Start:: Transaction Date -->
        <template v-slot:[`item.transaction_date`]="{ item }">
          <p>{{ formatDate(item.transaction_date) }}</p>
        </template>
        <!-- End:: Transaction Date -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <v-chip color="green" text-color="white" small>
            {{ $t("STATUS.success") }}
          </v-chip>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- View Details Button -->
            <a-tooltip
              v-if="$can('financialreports show', 'financialreports')"
              placement="bottom"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.viewDetails") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <!-- Download Tax Invoice Button -->
            <a-tooltip
              v-if="$can('financialreports show', 'financialreports')"
              placement="bottom"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.downloadTaxInvoice") }}</span>
              </template>
              <button class="btn_edit" @click="downloadTaxInvoice(item)">
                <i class="fal fa-file-invoice"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->

      <!-- ==== Start:: Financial Statistics Summary ==== -->
      <div class="table_content mt-5">
        <div class="table_title_wrapper">
          <div class="title_text_wrapper">
            <h5>{{ $t("TITLES.financialStatistics") }}</h5>
          </div>
        </div>
        <v-simple-table class="stat-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.totalOriginalPrice") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.totalTaxValue") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.totalPriceWithTax") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.totalSystemProfit") }}
                </th>
                <th class="text-center">
                  {{ $t("PLACEHOLDERS.totalTransactions") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="text-center all-stat">
                <td>{{ totalOriginalPrice }} {{ $t("PLACEHOLDERS.sar") }}</td>
                <td>{{ totalTaxValue }} {{ $t("PLACEHOLDERS.sar") }}</td>
                <td>{{ totalPriceWithTax }} {{ $t("PLACEHOLDERS.sar") }}</td>
                <td>{{ totalSystemProfit }} {{ $t("PLACEHOLDERS.sar") }}</td>
                <td>{{ totalTransactionsCount }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
      <!-- ==== End:: Financial Statistics Summary ==== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Generate PDF Template Content -->
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="$t('TITLES.financialReports')"
      :pdf-quality="2"
      pdf-format="a3"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      @progress="pdfDownloadBtnIsLoading = true"
      @hasGenerated="$message.success($t('MESSAGES.generatedSuccessfully'))"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">{{ $t("TITLES.financialReports") }}</h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>
                    <span>{{ $t("TABLES.Admins.serialNumber") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.userName") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.mobileNumber") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.userType") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.transactionType") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.numberOfDays") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.attendanceCount") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.originalPrice") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.taxValue") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.totalPrice") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.systemProfit") }}</span>
                  </th>
                  <th>
                    <span>{{ $t("PLACEHOLDERS.transactionDate") }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in tableRows" :key="row.id">
                  <td>{{ index + 1 }}</td>
                  <td>{{ row.user_name || "-" }}</td>
                  <td>{{ row.mobile_number || "-" }}</td>
                  <td>{{ getUserTypeText(row.user_type) }}</td>
                  <td>
                    {{ getTransactionTypeText(row.transactionable_type) }}
                  </td>
                  <td>{{ row.number_days || "-" }}</td>
                  <td>{{ row.number_attendance || "-" }}</td>
                  <td>{{ row.original_price }}</td>
                  <td>{{ row.tax_in_value }}</td>
                  <td>{{ row.total_price }}</td>
                  <td>{{ row.system_profit }}</td>
                  <td>{{ formatDate(row.transaction_date) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Financial Statistics Summary in PDF -->
          <div class="table_content mt-5">
            <v-simple-table class="stat-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.totalOriginalPrice") }}
                    </th>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.totalTaxValue") }}
                    </th>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.totalPriceWithTax") }}
                    </th>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.totalSystemProfit") }}
                    </th>
                    <th class="text-center">
                      {{ $t("PLACEHOLDERS.totalTransactions") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="text-center all-stat">
                    <td>{{ totalOriginalPrice }}</td>
                    <td>{{ totalTaxValue }}</td>
                    <td>{{ totalPriceWithTax }}</td>
                    <td>{{ totalSystemProfit }}</td>
                    <td>{{ totalTransactionsCount }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>
      </section>
    </vue-html2pdf>
    <!-- End:: Generate PDF Template Content -->
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";

export default {
  name: "AllFinancialReports",

  components: {
    VueHtml2pdf,
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    userTypes() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.individual"),
          value: "individual",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.store"),
          value: "store",
        },
      ];
    },

    transactionTypes() {
      return [
        {
          id: 1,
          name: this.$t("PLACEHOLDERS.appSubscription"),
          value: "UserSubscription",
        },
        {
          id: 2,
          name: this.$t("PLACEHOLDERS.adPackage"),
          value: "Announcement",
        },
      ];
    },
  },

  data() {
    return {
      // Financial Summary Data
      summaryData: {
        total_income: 0,
        individual_users_income: 0,
        service_providers_income: 0,
        total_vat_amount: 0,
        system_net_profit: 0,
        total_transactions_count: 0,
      },

      // Financial Statistics
      totalOriginalPrice: 0,
      totalTaxValue: 0,
      totalPriceWithTax: 0,
      totalSystemProfit: 0,
      totalTransactionsCount: 0,

      // Loading States
      loading: false,
      isWaitingRequest: false,
      pdfDownloadBtnIsLoading: false,
      excelDownloadBtnIsLoading: false,

      // Filter Data
      filterFormIsActive: false,
      filterOptions: {
        user_name: null,
        mobile_number: null,
        user_type: null,
        transaction_type: null,
        from_date: null,
        to_date: null,
      },

      // Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.userName"),
          value: "user_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.mobileNumber"),
          value: "mobile_number",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.userType"),
          value: "user_type",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.transactionType"),
          value: "transactionable_type",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.packageName"),
          value: "package_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.numberOfDays"),
          value: "number_days",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.attendanceCount"),
          value: "number_attendance",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.originalPrice"),
          value: "original_price",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.taxValue"),
          value: "tax_in_value",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.totalPrice"),
          value: "total_price",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.systemProfit"),
          value: "system_profit",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.transactionDate"),
          value: "transaction_date",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],

      // Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    // Filter Methods
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/financial-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions.user_name = null;
      this.filterOptions.mobile_number = null;
      this.filterOptions.user_type = null;
      this.filterOptions.transaction_type = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/financial-reports/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },

    // Pagination
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    // Fetch Summary Data
    async getSummaryData() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "financial_reports/summary",
        });

        this.summaryData = res.data.data;
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },

    // Fetch Data
    async setTableRows() {
      this.loading = true;

      try {
        let res = await this.$axios({
          method: "GET",
          url: "financial_reports",
          params: {
            page: this.paginations.current_page,
            user_name: this.filterOptions.user_name,
            mobile_number: this.filterOptions.mobile_number,
            type: this.filterOptions.user_type?.value,
            transactionable_type: this.filterOptions.transaction_type?.value,
            from: this.filterOptions.from_date,
            to: this.filterOptions.to_date,
          },
        });

        this.loading = false;
        this.tableRows = res.data.data.data;

        // Calculate totals
        this.calculateTotals();

        // Update pagination
        if (res.data.data.meta) {
          this.paginations.last_page = res.data.data.meta.last_page;
          this.paginations.items_per_page = res.data.data.meta.per_page;
        }
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
      }
    },

    // Calculate Financial Totals
    calculateTotals() {
      this.totalOriginalPrice = this.tableRows
        .reduce((sum, item) => sum + parseFloat(item.original_price || 0), 0)
        .toFixed(2);

      this.totalTaxValue = this.tableRows
        .reduce((sum, item) => sum + parseFloat(item.tax_in_value || 0), 0)
        .toFixed(2);

      this.totalPriceWithTax = this.tableRows
        .reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0)
        .toFixed(2);

      this.totalSystemProfit = this.tableRows
        .reduce((sum, item) => sum + parseFloat(item.system_profit || 0), 0)
        .toFixed(2);

      this.totalTransactionsCount = this.tableRows.length;
    },

    // Helper Methods for Display
    getUserTypeText(type) {
      if (type === "individual") {
        return this.$t("PLACEHOLDERS.individual");
      } else if (type === "store") {
        return this.$t("PLACEHOLDERS.store");
      }
      return "-";
    },

    getUserTypeColor(type) {
      if (type === "individual") {
        return "blue";
      } else if (type === "store") {
        return "purple";
      }
      return "grey";
    },

    getTransactionTypeText(type) {
      if (type === "UserSubscription") {
        return this.$t("PLACEHOLDERS.appSubscription");
      } else if (type === "Announcement") {
        return this.$t("PLACEHOLDERS.adPackage");
      }
      return "-";
    },

    getTransactionTypeColor(type) {
      if (type === "UserSubscription" || type === "إعلان") {
        return "teal";
      } else if (type === "Announcement" || type === "باقة") {
        return "orange";
      }
      return "grey";
    },

    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString(
        this.getAppLocale === "ar" ? "ar-SA" : "en-US",
        {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }
      );
    },

    // Actions
    showItem(item) {
      this.$router.push({ path: `/financial-reports/show/${item.id}` });
    },

    async downloadTaxInvoice(item) {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `financial-transactions/${item.id}/invoice`,
        });

        if (res.data.invoice_url) {
          window.open(res.data.invoice_url, "_blank");
        } else {
          this.$message.error(this.$t("MESSAGES.errorOccurred"));
        }
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
        this.$message.error(this.$t("MESSAGES.errorOccurred"));
      }
    },

    // Excel Download
    async downloadExcel() {
      this.excelDownloadBtnIsLoading = true;

      try {
        let res = await this.$axios({
          method: "GET",
          url: "financial-reports/export",
          params: {
            user_name: this.filterOptions.user_name,
            mobile: this.filterOptions.mobile_number,
            user_type: this.filterOptions.user_type?.value,
            transactionable_type: this.filterOptions.transaction_type?.value,
            from_date: this.filterOptions.from_date,
            to_date: this.filterOptions.to_date,
          },
          responseType: "blob",
        });

        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `financial_reports_${new Date().getTime()}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        this.excelDownloadBtnIsLoading = false;
        this.$message.success(this.$t("MESSAGES.generatedSuccessfully"));
      } catch (error) {
        this.excelDownloadBtnIsLoading = false;
        console.log(error.response?.data?.message || error.message);
        this.$message.error(this.$t("MESSAGES.errorOccurred"));
      }
    },

    // PDF Download
    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
      this.pdfDownloadBtnIsLoading = false;
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });

    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }

    this.getSummaryData();
    this.setTableRows();
  },
};
</script>

<style scoped>
.ex-btn-s {
  color: black !important;
  font-size: 20px;
  font-weight: bold;
  text-decoration: underline !important;
}

.all-stat {
  background: var(--main_theme_clr);
}

.all-stat td {
  color: white;
  font-size: 21px !important;
  font-weight: 800;
}

.show_all_content_wrapper
  .v-data-table
  .v-data-table__wrapper
  table
  tbody
  tr:hover {
  background: var(--main_theme_clr) !important;
}

.excel {
  background: darkgreen;
  padding: 7px 10px;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
}

.excel_btn {
  margin-right: 10px;
}

.excel_btn >>> .v-btn {
  background: #1d6f42 !important;
}

.excel_btn >>> .v-btn:hover {
  background: #155a35 !important;
}

/* Financial Summary Cards */
.financial_summary_wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px 0;
}

.summary_card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 25px;
  border-radius: 12px;
  background: #667eea;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  color: white;
  background-color: #f7941e;
}

.summary_card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.summary_card .card_icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 28px;
}

.summary_card .card_content {
  flex: 1;
}

.summary_card .card_content h6 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
}

.summary_card .card_content .amount {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

@media (max-width: 768px) {
  .financial_summary_wrapper {
    grid-template-columns: 1fr;
  }
}
</style>
